import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isCheckoutSessionTerminalStatus } from '../api/checkout';
import type { CheckoutResponse, CheckoutSessionResponse } from '../api/checkout';
import { copyToClipboard } from '../utils/clipboard';
import { sessionPayDisplay } from '../utils/sessionDisplay';
import CountdownTimer from './CountdownTimer';
import OrderSummaryRail from './OrderSummaryRail';
import QRCodeDisplay from './QRCodeDisplay';
import StatusPoller from './StatusPoller';

export interface PaymentViewProps {
  checkout: CheckoutResponse;
  session: CheckoutSessionResponse;
  onSessionUpdate: (session: CheckoutSessionResponse) => void;
  onBack: () => void;
  onTerminal: () => void;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onCopy = async () => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className="ps-copy"
      onClick={() => void onCopy()}
      style={copied ? { color: '#16a34a' } : undefined}
    >
      <Icon icon={copied ? 'mdi:check' : 'mdi:content-copy'} width={16} height={16} />
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export default function PaymentView({
  checkout,
  session: initial,
  onSessionUpdate,
  onBack,
  onTerminal,
}: PaymentViewProps) {
  const [session, setSession] = useState(initial);
  const [expiredLocal, setExpiredLocal] = useState(false);

  useEffect(() => {
    setSession(initial);
    setExpiredLocal(false);
  }, [initial]);

  const amountStr = String(session.amountRequired);
  const expiresAt = new Date(session.expiresAt);
  const amountReceived = Number(session.amountReceived);
  const amountRequired = Number(session.amountRequired);
  const remaining = Math.max(0, amountRequired - amountReceived).toFixed(8);
  const partialPct =
    amountRequired > 0 ? Math.min(100, Math.round((amountReceived / amountRequired) * 100)) : 0;
  const usd = Number(checkout.priceUsdSnapshot).toFixed(2);
  const swap = session.swap;

  // What the payer must actually send. On a swap-funded session the session's
  // own address and amount describe the leg they never see, so both are
  // replaced — the address is the quote-bound one, and the amount, symbol and
  // chain come from `sessionPayDisplay`, shared with the confirmation screen so
  // the two screens cannot disagree about what the payer paid with.
  const payAddress = swap?.depositAddress ?? session.depositAddress;
  const {
    amount: payAmount,
    symbol: paySymbol,
    chainLabel: payChainLabel,
  } = sessionPayDisplay(session);

  // A swap-funded session cannot be topped up, so the "send the rest to the
  // same address" branch would be actively harmful: it points at an address
  // that is already spent. Such a session falls through to the ordinary
  // deposit panel instead.
  const isPartial = session.status === 'PARTIAL' && swap === null;
  const payMint = checkout.acceptedNetworksTokensSnapshot.find(
    (p) => p.network === session.network && p.token === session.token,
  );

  const handlePoll = useCallback(
    (s: CheckoutSessionResponse) => {
      setSession(s);
      onSessionUpdate(s);
      if (s.status === 'EXPIRED') setExpiredLocal(true);
      if (isCheckoutSessionTerminalStatus(s.status)) onTerminal();
    },
    [onSessionUpdate, onTerminal],
  );

  return (
    <div className="ps-page">
      <StatusPoller checkoutId={checkout.id} sessionId={session.id} onSession={handlePoll} />
      <div className="ps-shell">
        <div className="ps-body">
          <OrderSummaryRail checkout={checkout}>
            {isPartial ? (
              <div className="ps-partial-card" style={{ marginBottom: 20 }}>
                <div className="ps-kicker" style={{ color: '#92400e' }}>Partial received</div>
                <div className="ps-amount-big" style={{ fontSize: 22 }}>
                  {session.amountReceived} / {amountStr} {session.token}
                </div>
                <div className="ps-progress">
                  <div className="ps-progress-bar" style={{ width: `${partialPct}%` }} />
                </div>
                <div style={{ color: '#92400e', fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                  {partialPct}% received — send remaining
                </div>
              </div>
            ) : (
              <div>
                <div className="ps-kicker">Amount due</div>
                <div className="ps-amount-big">
                  {payAmount ?? '—'} {paySymbol}
                </div>
                <div className="ps-subtitle" style={{ margin: 0 }}>≈ ${usd} USD</div>
                <div className="ps-inline-time">
                  <Icon icon="mdi:clock-outline" width={16} height={16} />
                  <CountdownTimer expiresAt={expiresAt} onExpired={() => setExpiredLocal(true)} variant="inline" />
                  <span style={{ fontWeight: 400, color: 'var(--ps-muted)' }}>remaining</span>
                </div>
              </div>
            )}
            <div className="ps-pay-with" style={{ marginTop: 10, marginBottom: 24 }}>
              {payMint?.image ? (
                <img src={payMint.image} alt="" className="ps-pay-with-icon" />
              ) : null}
              <span>
                {payChainLabel} · {paySymbol}
              </span>
            </div>
          </OrderSummaryRail>

          {isPartial ? (
            <section className="ps-main">
              <div className="ps-kicker" style={{ color: '#d97706' }}>Partial payment detected</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>We received part of your payment</div>
              <div className="ps-subtitle" style={{ marginTop: 0 }}>
                Please send the remaining amount to complete your order.
              </div>
              <div className="ps-kv-card">
                <div className="ps-kv-row">
                  <span className="ps-muted">Received</span>
                  <span className="ps-kv-value" style={{ color: '#16a34a' }}>
                    {session.amountReceived} {session.token}
                  </span>
                </div>
                <div className="ps-kv-row">
                  <span className="ps-muted">Remaining</span>
                  <span className="ps-kv-value" style={{ color: '#dc2626' }}>
                    {remaining} {session.token}
                  </span>
                </div>
                <div className="ps-kv-row">
                  <span className="ps-muted">Required total</span>
                  <span className="ps-kv-value">
                    {amountStr} {session.token}
                  </span>
                </div>
              </div>
              <div className="ps-data">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div className="ps-data-label">Deposit address (same)</div>
                  <CopyButton text={session.depositAddress} />
                </div>
                <div className="ps-data-value">{session.depositAddress}</div>
              </div>
              <div className="ps-alert warning" style={{ marginTop: 'auto' }}>
                <strong>Send remaining {remaining} {session.token} to the same address</strong>
                <div>The address and session are still active</div>
              </div>
            </section>
          ) : (
            <section className="ps-main">
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Scan the QR code or copy the details below
              </div>
              <div className="ps-pay-qr">
                <div className="ps-qr-wrap">
                  {/*
                    Bare address, deliberately. The chains Cheddar models have
                    BIP-21 / Solana Pay / EIP-681 URIs, but a deposit address on
                    an arbitrary chain has no scheme we can build, and a wrong
                    scheme is worse than none — so every QR here encodes the
                    address itself.
                  */}
                  <QRCodeDisplay address={payAddress} size={176} />
                </div>
              </div>
              <div className="ps-data">
                <div className="ps-field-head">
                  <div className="ps-data-label">Deposit address</div>
                  <CopyButton text={payAddress} />
                </div>
                <div className="ps-data-value">{payAddress}</div>
              </div>
              {/*
                The memo carries the same weight as the address and its own copy
                button: on the chains that use one, a deposit sent without it is
                lost.
              */}
              {swap?.depositMemo ? (
                <div className="ps-data">
                  <div className="ps-field-head">
                    <div className="ps-data-label">Required memo</div>
                    <CopyButton text={swap.depositMemo} />
                  </div>
                  <div className="ps-data-value">{swap.depositMemo}</div>
                </div>
              ) : null}
              {payAmount === null ? (
                <div className="ps-alert warning">
                  <strong>Amount unavailable</strong>
                  <div>Please refresh this page before sending anything.</div>
                </div>
              ) : (
                <div className="ps-data">
                  <div className="ps-field-head">
                    <div className="ps-data-label">Amount (exact)</div>
                    <CopyButton text={payAmount} />
                  </div>
                  <div className="ps-data-value">
                    {payAmount} {paySymbol}
                  </div>
                </div>
              )}
              {swap?.depositMemo ? (
                <div className="ps-alert warning">
                  <strong>Include the memo with your payment</strong>
                  <div>A payment sent without it cannot be recovered.</div>
                </div>
              ) : null}
              <div className="ps-note">
                <Icon icon="mdi:alert-outline" width={16} height={16} className="ps-note-icon" />
                Send the exact amount shown. Incorrect amounts will cause delays.
              </div>
              {expiredLocal ? (
                <button type="button" className="ps-button outline" style={{ marginTop: 'auto' }} onClick={onBack}>
                  Start new payment
                </button>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
